import { classNames } from '@dimjs/utils';
import { hooks } from '@wove/react';
import { forwardRef, Fragment, ReactElement, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { useLockFn } from '../hooks';
import { InVisibleView } from '../in-visible-view';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { Progress } from '../progress';
import { Space } from '../space';
import { getScrollParent } from '../utils/get-scroll-parent';
import { NativeProps } from '../utils/native-props';
import './style.less';

const classPrefix = `v-infinite-scroll`;

export type NativeInfiniteScrollProps = {
  onLoadMore: () => Promise<any>;
  hasMore: boolean;
  // 目前实现有问题，暂时不支持
  // threshold?: number;
  visible?: boolean;
  children?: ReactNode | ReactNode[];
  noMoreText?: string | ReactElement;
} & NativeProps;

const InfiniteScrollContent = (props: {
  hasMore: boolean;
  noMoreText: NativeInfiniteScrollProps['noMoreText'];
}) => {
  return (
    <>
      {props.hasMore ? (
        <Space gap={10}>
          <Progress value={0.1} gap={3} size={30} isRotate={true} />
          <span>加载中...</span>
        </Space>
      ) : (
        <div className="v-infinite-scroll-nomore">{props.noMoreText}</div>
      )}
    </>
  );
};

export type NativeInfiniteScrollRefApi = {
  scrollToTop: () => void;
};

export const NativeInfiniteScroll = forwardRef<NativeInfiniteScrollRefApi, NativeInfiniteScrollProps>(
  (props, ref) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    // const threshold = typeof props.threshold === 'number' ? px2DPIpx(props.threshold) : '0px';

    const doLoadMore = useLockFn(() => {
      return props.onLoadMore();
    });

    const onChangeInView = hooks.useDebounceClick((inView) => {
      if (inView && !loadFailed) {
        if (!props.visible || !props.hasMore) return;
        void doLoadMore().catch(() => {
          setLoadFailed(true);
        });
      }
    }, 200);

    const onChangeInView2 = hooks.useCallbackRef((inView) => {
      if (!inView) {
        setLoadFailed(false);
      }
    });

    const onFailedRequest = hooks.useCallbackRef(() => {
      setLoadFailed(false);
      void doLoadMore().catch(() => {
        setLoadFailed(true);
      });
    });

    useImperativeHandle(ref, () => {
      return {
        scrollToTop: () => {
          const element = getScrollParent(elementRef.current as Element) as Element;
          element.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        },
      };
    });

    if (!props.visible) return null;

    return (
      <Fragment>
        {/* <InVisibleView onChange={onChangeInView} rootMargin={`0px 0px ${threshold} 0px`} /> */}
        <InVisibleView onChange={onChangeInView} />
        <div className={classNames(classPrefix, props.className)} style={props.style} ref={elementRef}>
          {loadFailed ? (
            <Fragment>
              <InVisibleView onChange={onChangeInView2} rootMargin={`0px 0px 30px 0px`} />
              <div onClick={onFailedRequest} className={`${classPrefix}-failed`}>
                数据加载失败，点击重新加载
              </div>
            </Fragment>
          ) : (
            <Fragment>
              {props.children ? (
                props.children
              ) : (
                <InfiniteScrollContent hasMore={props.hasMore} noMoreText={props.noMoreText} />
              )}
            </Fragment>
          )}
        </div>
        <IphoneBottomAdaptive />
      </Fragment>
    );
  },
);

NativeInfiniteScroll.defaultProps = {
  visible: false,
  noMoreText: '没有更多数据了',
};
